import React, { useState } from 'react';
import { CheckSquare, Square, Plus, Trash2, X, Check } from 'lucide-react';
import axios from 'axios';
import { getApiUrl } from '../../services/api';
import { PHASES, PHASE_COLOR } from './PhaseTimeline';

const PHASE_LABELS = Object.fromEntries(PHASES.map(p => [p.key, p.label]));

const TasksPanel = ({ opportunity, token, onUpdate }) => {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: '', phase: opportunity.phase || 'exploration', dueDate: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.dueDate) delete payload.dueDate;
      const { data } = await axios.post(
        `${getApiUrl()}/opportunities/${opportunity._id}/tasks`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate(data);
      setForm({ title: '', phase: opportunity.phase || 'exploration', dueDate: '', notes: '' });
      setAdding(false);
    } catch (e) {
      console.error('Error adding task', e);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (taskId, done) => {
    try {
      const { data } = await axios.put(
        `${getApiUrl()}/opportunities/${opportunity._id}/tasks/${taskId}`,
        { done },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate(data);
    } catch (e) {
      console.error('Error toggling task', e);
    }
  };

  const handleDelete = async (taskId) => {
    try {
      const { data } = await axios.delete(
        `${getApiUrl()}/opportunities/${opportunity._id}/tasks/${taskId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate(data);
    } catch (e) {
      console.error('Error deleting task', e);
    }
  };

  const tasks = opportunity.tasks || [];
  // Group by phase
  const grouped = PHASES.reduce((acc, p) => {
    acc[p.key] = tasks.filter(t => t.phase === p.key);
    return acc;
  }, {});
  const ungrouped = tasks.filter(t => !t.phase || !PHASES.find(p => p.key === t.phase));

  const inputStyle = {
    width: '100%',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '6px',
    color: '#F0F0F0',
    padding: '8px 10px',
    fontSize: '0.82rem',
    outline: 'none',
    marginBottom: '6px',
    boxSizing: 'border-box',
  };

  const TaskRow = ({ task }) => (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      padding: '8px 10px',
      borderRadius: '6px',
      background: task.done ? 'rgba(255,255,255,0.01)' : 'rgba(255,255,255,0.03)',
      marginBottom: '6px',
      border: '1px solid rgba(255,255,255,0.05)',
    }}>
      <button
        onClick={() => handleToggle(task._id, !task.done)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: task.done ? '#27AE60' : '#555', flexShrink: 0, padding: 0, marginTop: 1 }}
      >
        {task.done ? <CheckSquare size={15} /> : <Square size={15} />}
      </button>
      <div style={{ flex: 1 }}>
        <span style={{
          fontSize: '0.85rem',
          color: task.done ? '#555' : '#CCC',
          textDecoration: task.done ? 'line-through' : 'none',
        }}>
          {task.title}
        </span>
        {task.dueDate && (
          <div style={{ fontSize: '0.65rem', color: '#555', marginTop: '2px', fontFamily: 'monospace' }}>
            {new Date(task.dueDate).toLocaleDateString('es-ES')}
          </div>
        )}
      </div>
      <button
        onClick={() => handleDelete(task._id)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#333', padding: 0, flexShrink: 0 }}
        onMouseOver={(e) => { e.currentTarget.style.color = '#E74C3C'; }}
        onMouseOut={(e) => { e.currentTarget.style.color = '#333'; }}
      >
        <Trash2 size={12} />
      </button>
    </div>
  );

  const allGrouped = [
    ...PHASES.map(p => ({ ...p, items: grouped[p.key] })).filter(g => g.items.length > 0),
    ...(ungrouped.length > 0 ? [{ key: 'other', label: 'Sin fase', color: '#555', items: ungrouped }] : []),
  ];

  return (
    <div>
      {tasks.length === 0 && !adding && (
        <p style={{ color: '#444', fontSize: '0.8rem', fontStyle: 'italic', marginBottom: '12px' }}>No hay tareas registradas.</p>
      )}

      {/* Grouped task list */}
      {allGrouped.map(group => (
        <div key={group.key} style={{ marginBottom: '16px' }}>
          <div style={{
            fontSize: '0.65rem',
            color: group.color,
            fontWeight: '700',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            marginBottom: '6px',
            borderBottom: `1px solid ${group.color}33`,
            paddingBottom: '4px',
          }}>
            {group.label}
          </div>
          {group.items.map(task => <TaskRow key={task._id} task={task} />)}
        </div>
      ))}

      {/* Add form */}
      {adding ? (
        <div style={{ background: 'rgba(226,123,88,0.06)', border: '1px solid rgba(226,123,88,0.25)', borderRadius: '8px', padding: '14px', marginTop: '8px' }}>
          <input style={inputStyle} placeholder="Descripción de la tarea *" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
          <select
            style={{ ...inputStyle, cursor: 'pointer' }}
            value={form.phase}
            onChange={e => setForm({ ...form, phase: e.target.value })}
          >
            {PHASES.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
          <input style={inputStyle} type="date" value={form.dueDate} onChange={e => setForm({ ...form, dueDate: e.target.value })} />
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button onClick={() => setAdding(false)} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#888', padding: '6px 14px', cursor: 'pointer', fontSize: '0.8rem' }}>
              Cancelar
            </button>
            <button onClick={handleAdd} disabled={saving || !form.title.trim()} style={{ background: '#E27B58', border: 'none', borderRadius: '6px', color: 'white', padding: '6px 16px', cursor: 'pointer', fontWeight: '700', fontSize: '0.8rem', opacity: saving ? 0.7 : 1 }}>
              Guardar
            </button>
          </div>
        </div>
      ) : (
        <button onClick={() => setAdding(true)} style={{ display: 'flex', alignItems: 'center', gap: '7px', background: 'transparent', border: '1px dashed rgba(226,123,88,0.4)', borderRadius: '6px', color: '#E27B58', padding: '9px 16px', cursor: 'pointer', fontSize: '0.82rem', width: '100%', justifyContent: 'center' }}>
          <Plus size={14} /> Añadir tarea
        </button>
      )}
    </div>
  );
};

export default TasksPanel;
